import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, ChevronRight, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`);
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, navigate]);

  const getStatusIcon = (status, paymentStatus) => {
    if (paymentStatus === 'paid' || status === 'confirmed') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    return <Clock className="w-5 h-5 text-yellow-600" />;
  };

  const getStatusText = (status, paymentStatus) => {
    if (paymentStatus === 'paid') return 'Paid';
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'pending') return 'Pending';
    return status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 bg-[#E8EFE9] rounded-3xl skeleton"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-sm">
            <Package className="w-16 h-16 text-[#6B7280] mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[#1A3C34] font-['Chivo'] mb-4">
              No orders yet
            </h1>
            <p className="text-[#6B7280] mb-8">
              You haven't placed any orders. Start shopping to fill this page!
            </p>
            <Link to="/products">
              <Button 
                className="rounded-full bg-[#D93052] text-white font-bold px-8 py-4 hover:bg-[#B02040]"
                data-testid="orders-shop-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Start Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-[#1A3C34] font-['Chivo'] mb-8">
          Order History
        </h1>

        <div className="space-y-4">
          {orders.map((order) => (
            <div 
              key={order.id}
              className="bg-white rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`order-${order.id}`}
            >
              {/* Order Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-[#E8EFE9]">
                <div>
                  <p className="text-sm text-[#6B7280]">Order placed</p>
                  <p className="font-medium text-[#1A3C34]">{formatDate(order.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(order.status, order.payment_status)}
                  <span className={`font-medium ${
                    order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {getStatusText(order.status, order.payment_status)}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-3 mb-4">
                {order.items.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#E8EFE9] shrink-0">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name || 'Product'}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A3C34] truncate">
                        {item.name || 'Product'}
                      </p>
                      <p className="text-sm text-[#6B7280]">
                        Qty: {item.quantity} × ${item.price?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                ))}
                {order.items.length > 3 && (
                  <p className="text-sm text-[#6B7280]">
                    + {order.items.length - 3} more item(s)
                  </p>
                )}
              </div>

              {/* Order Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-[#E8EFE9]">
                <div>
                  <p className="text-sm text-[#6B7280]">Total</p>
                  <p className="text-xl font-bold text-[#1A3C34]">
                    ${order.total.toFixed(2)}
                  </p>
                </div>
                <Link to={`/orders/${order.id}`}>
                  <Button 
                    variant="ghost"
                    className="text-[#D93052] font-medium hover:bg-[#D93052]/10"
                  >
                    View Details
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Orders;
